import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Mail, MapPin, ShieldCheck, FileText, X } from 'lucide-react';
export function Footer() {
  const year = new Date().getFullYear();
  const [legalOpen, setLegalOpen] = useState(false);
  // Lock body scroll + close on ESC when modal is open
  useEffect(() => {
    if (!legalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLegalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [legalOpen]);
  return <>
      <footer className="bg-navy-950 text-navy-200 border-t border-navy-800" data-id="element-48">
        <div className="max-w-5xl mx-auto px-4 py-14" data-id="element-49">
          {/* Top — brand */}
          <div className="grid md:grid-cols-3 gap-10 md:gap-8" data-id="element-50">
            {/* Brand */}
            <div className="md:col-span-1" data-id="element-51">
              <p className="font-serif text-2xl font-bold text-white" data-id="element-52">
                Inercia Cero
              </p>
              <p className="text-sm text-navy-300 mt-3 leading-relaxed" data-id="element-53">
                Una mentoría para restaurar orden en tu relación con Dios, con
                el dinero y contigo mismo.
              </p>
              <p className="text-xs text-navy-400 mt-4" data-id="element-54">
                Fundado por <span className="text-gold-300" data-id="element-55">Paul Ocampo</span>
              </p>
            </div>

            {/* Contacto */}
            <div data-id="element-56">
              <h3 className="text-sm font-bold tracking-widest uppercase text-gold-400 mb-4" data-id="element-57">
                Contacto
              </h3>
              <ul className="space-y-3 text-sm" data-id="element-58">
                <li className="flex items-start gap-2.5" data-id="element-59">
                  <Mail className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" data-id="element-60" />
                  <a href="mailto:inerciacero.ok@gmail.com" className="hover:text-white transition-colors" data-id="element-61">
                    inerciacero.ok@gmail.com
                  </a>
                </li>
                <li className="flex items-start gap-2.5" data-id="element-62">
                  <MapPin className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" data-id="element-63" />
                  <span data-id="element-64">Argentina · Atendemos LATAM</span>
                </li>
              </ul>
            </div>

            {/* Garantía resumen */}
            <div data-id="element-65">
              <h3 className="text-sm font-bold tracking-widest uppercase text-gold-400 mb-4" data-id="element-66">
                Compra segura
              </h3>
              <div className="flex items-start gap-2.5 text-sm" data-id="element-67">
                <ShieldCheck className="w-4 h-4 text-gold-400 mt-0.5 shrink-0" data-id="element-68" />
                <p className="leading-relaxed" data-id="element-69">
                  Si al final de la charla no recibís valor real, no pagás nada.
                  Pago procesado de forma segura a través de Shopify.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom strip */}
          <div className="mt-12 pt-6 border-t border-navy-800 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-navy-400" data-id="element-70">
            <p className="order-2 md:order-1" data-id="element-71">
              © {year} Inercia Cero · Paul Ocampo. Todos los derechos
              reservados.
            </p>
            <button type="button" onClick={() => setLegalOpen(true)} className="order-1 md:order-2 inline-flex items-center gap-2 text-navy-300 hover:text-gold-300 transition-colors group" data-id="element-72">
              <FileText className="w-3.5 h-3.5" data-id="element-73" />
              <span className="underline underline-offset-4 decoration-navy-700 group-hover:decoration-gold-400" data-id="element-74">
                Ver Aviso Legal
              </span>
            </button>
          </div>
        </div>
      </footer>

      {/* Aviso Legal — Modal */}
      <AnimatePresence data-id="element-75">
        {legalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4" role="dialog" aria-modal="true" aria-labelledby="legal-title" data-id="element-76">
            {/* Backdrop */}
            <button type="button" aria-label="Cerrar" onClick={() => setLegalOpen(false)} className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm cursor-default" data-id="element-77" />

            {/* Dialog */}
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} transition={{
          duration: 0.28,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-full md:max-w-2xl max-h-[90vh] md:max-h-[85vh] bg-white text-navy-900 rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col" data-id="element-78">
              {/* Header */}
              <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-navy-100 bg-white" data-id="element-79">
                <div className="flex items-center gap-3" data-id="element-80">
                  <div className="w-9 h-9 rounded-full bg-gold-100 text-gold-600 flex items-center justify-center" data-id="element-81">
                    <FileText className="w-4 h-4" data-id="element-82" />
                  </div>
                  <h2 id="legal-title" className="font-serif text-xl md:text-2xl font-bold text-navy-900" data-id="element-83">
                    Aviso Legal
                  </h2>
                </div>
                <button type="button" onClick={() => setLegalOpen(false)} aria-label="Cerrar aviso legal" className="w-9 h-9 rounded-full hover:bg-navy-50 flex items-center justify-center text-navy-600 hover:text-navy-900 transition-colors" data-id="element-84">
                  <X className="w-5 h-5" data-id="element-85" />
                </button>
              </div>

              {/* Body (scrollable) */}
              <div className="overflow-y-auto px-6 md:px-8 py-6 space-y-4 text-sm md:text-[15px] text-navy-700 leading-relaxed" data-id="element-86">
                <p data-id="element-87">
                  El contenido de este sitio web y los servicios ofrecidos por
                  Inercia Cero y Paul Ocampo tienen un fin{' '}
                  <span className="text-navy-900 font-semibold" data-id="element-88">
                    educativo, formativo y de acompañamiento personal
                  </span>
                  . No constituyen asesoramiento financiero, legal, médico,
                  psicológico ni terapéutico profesional, ni reemplazan la
                  consulta con profesionales matriculados en dichas áreas.
                </p>

                <p data-id="element-89">
                  Los{' '}
                  <span className="text-navy-900 font-semibold" data-id="element-90">
                    resultados
                  </span>{' '}
                  obtenidos a partir de las sesiones, mentorías y materiales
                  dependen del compromiso, contexto y acción personal de cada
                  participante. No garantizamos resultados específicos —
                  espirituales, financieros, ni de transformación personal.
                  Cualquier testimonio mostrado es individual y no implica una
                  expectativa de resultados idénticos.
                </p>

                <p data-id="element-91">
                  Al realizar la compra, el cliente acepta las{' '}
                  <span className="text-navy-900 font-semibold" data-id="element-92">
                    condiciones de servicio
                  </span>{' '}
                  y la política de reembolso aquí descritas. La{' '}
                  <span className="text-navy-900 font-semibold" data-id="element-93">
                    garantía sin riesgo
                  </span>{' '}
                  aplica únicamente cuando, al finalizar la sesión, el cliente
                  manifieste expresamente no haber recibido valor; el reembolso
                  se procesa por la misma vía de pago original.
                </p>

                <p data-id="element-94">
                  Los datos personales recolectados (nombre, email, datos de
                  contacto y pago) se utilizan exclusivamente para coordinar la
                  sesión, enviarte material complementario y cumplir con
                  obligaciones contables. No compartimos tus datos con terceros
                  ajenos al proceso de prestación del servicio. Podés solicitar
                  la baja o eliminación de tus datos en cualquier momento
                  escribiendo a{' '}
                  <a href="mailto:inerciacero.ok@gmail.com" className="text-gold-600 hover:text-gold-700 font-medium underline underline-offset-2" data-id="element-95">
                    inerciacero.ok@gmail.com
                  </a>
                  .
                </p>

                <p data-id="element-96">
                  Este sitio puede utilizar{' '}
                  <span className="text-navy-900 font-semibold" data-id="element-97">
                    cookies y píxeles de seguimiento
                  </span>{' '}
                  (Meta Pixel) para medir el rendimiento de campañas
                  publicitarias y mejorar la experiencia del usuario. Al
                  continuar navegando, aceptás su uso.
                </p>

                <p className="italic text-navy-500 pt-2" data-id="element-98">
                  Inercia Cero se reserva el derecho de actualizar este aviso
                  legal en cualquier momento. La última actualización fue el{' '}
                  {new Date().toLocaleDateString('es-AR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
                  .
                </p>
              </div>

              {/* Footer */}
              <div className="px-6 md:px-8 py-4 border-t border-navy-100 bg-navy-50 flex justify-end" data-id="element-99">
                <button type="button" onClick={() => setLegalOpen(false)} className="px-5 py-2.5 rounded-full bg-navy-900 hover:bg-navy-800 text-white text-sm font-semibold transition-colors" data-id="element-100">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}